/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React, { useMemo, useState } from "react";
import CellJob from "./CellJob";
import "./TableScheduler.scss";

const getStatusClass = (status) => {
  const s = (status || "").trim().toUpperCase();
  if (s.includes("WAIT")) return "status-waiting";
  if (s.includes("RUN")) return "status-running";
  if (s === "OK") return "status-ok";
  if (s.includes("ERR")) return "status-error";
  if (s.includes("WARN")) return "status-warning";
  if (s.includes("HOLD")) return "status-onhold";
  if (s.includes("SKIP")) return "status-skip";
  return "status-other";
};

const TableScheduler = ({
  schedulerSelected,
  dictJobs,
  listJobSelected = [],
  statusFilter = "",
  searchTerm = "",
  sortField = "Status",
  sortOrder = "asc",
  onSortChange,
  onJobSelectionChange,
  onStatusChangeRequest,
  onAuthError,
  idToken,
  getToken,
}) => {
  const [expandedJobId, setExpandedJobId] = useState(null);

  const listJobs = useMemo(() => {
    if (schedulerSelected === "ALL") {
      return Object.values(dictJobs).flat();
    }
    return dictJobs[schedulerSelected] || [];
  }, [dictJobs, schedulerSelected]);

  // Filter the jobs using searchTerm and statusFilter.
  const filteredJobs = useMemo(() => {
    const searchComponents = searchTerm
      .toLowerCase()
      .split(" ")
      .filter(Boolean);
    return listJobs.filter((job) => {
      const s = (job.Status || "").trim().toUpperCase();

      // Check if all search components match
      const allComponentsMatch = searchComponents.every((component) => {
        return (
          (job.Name && job.Name.toLowerCase().includes(component)) ||
          (job.Status && job.Status.toLowerCase().includes(component)) ||
          (job.SchedulerName &&
            job.SchedulerName.toLowerCase().includes(component))
        );
      });

      if (!allComponentsMatch) return false;
      if (!statusFilter) return true;
      if (s.includes("WAIT")) return statusFilter.includes("WAIT");
      if (s.includes("RUN") || s.includes("START") || s.includes("TERM"))
        return statusFilter.includes("RUN");
      if (s.includes("OK")) return statusFilter.includes("OK");
      if (s.includes("WARN")) return statusFilter.includes("WARN");
      if (s.includes("ERR")) return statusFilter.includes("ERR");
      if (s.includes("HOLD")) return statusFilter.includes("HOLD");
      if (s.includes("SKIP")) return statusFilter.includes("SKIP");
      return true;
    });
  }, [listJobs, searchTerm, statusFilter]);

  const mappedJobs = useMemo(
    () =>
      filteredJobs.map((job) => ({
        id: job.SchedulerName + "_" + job.Name,
        Name: job.Name,
        Status: job.Status,
        StartTime: job.StartTime,
        EndTime: job.EndTime,
        CommandLine: job.CommandLine,
        FullCommandLine: job.FullCommandLine,
        Dependencies: job.Dependencies,
        ApplicationId: job.ApplicationId,
        Children: job.Children,
        Frequency: job.Frequency,
        Hostgroup: job.Hostgroup,
        Runs: job.Attempts,
        Hostname: job.Hostname,
        Group: job.Group,
        Duration: job.Duration,
        JobType: job.JobType,
        Resources: job.Resources,
        RunAs: job.RunAs,
        SchedulerName: job.SchedulerName,
        AlgoVarsFilepath: job.AlgoVarsFilepath,
        MinStartTime: job.MinStartTime,
      })),
    [filteredJobs]
  );

  const sortedJobs = useMemo(() => {
    return [...mappedJobs].sort((a, b) => {
      let valA = a[sortField] || "";
      let valB = b[sortField] || "";
      if (sortField === "Runs") {
        valA = parseInt(valA, 10) || 0;
        valB = parseInt(valB, 10) || 0;
      } else {
        valA = valA.toString().toLowerCase();
        valB = valB.toString().toLowerCase();
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [mappedJobs, sortField, sortOrder]);

  // Helper to check if a job is in the selected list from the parent
  const isJobSelected = (job) => {
    return listJobSelected.some((selected) => selected.id === job.id);
  };

  const handleRowClick = (jobId, e) => {
    if (e.target.tagName.toLowerCase() === "input") return;
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const handleCheckboxChange = (job) => {
    // If the job is selected, remove it; otherwise, add it
    const currentlySelected = isJobSelected(job);
    let newSelected;
    if (currentlySelected) {
      newSelected = listJobSelected.filter((sel) => sel.id !== job.id);
    } else {
      newSelected = [...listJobSelected, job];
    }
    onJobSelectionChange(newSelected);
  };

  const handleSelectAllChange = () => {
    const allSelected = listJobSelected.length === sortedJobs.length;
    if (allSelected) {
      // Clear all selections
      onJobSelectionChange([]);
    } else {
      // Select all
      onJobSelectionChange(sortedJobs);
    }
  };

  const handleSort = (field) => {
    onSortChange(field);
  };

  return (
    <div className="scheduler-table-container">
      <table className="scheduler-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={handleSelectAllChange}
                checked={
                  sortedJobs.length > 0 &&
                  listJobSelected.length === sortedJobs.length
                }
              />
            </th>
            {[
              "Status",
              "Name",
              "Scheduler",
              "StartTime",
              "EndTime",
              "Duration",
              "Runs",
            ].map((field) => (
              <th
                key={field}
                onClick={() => handleSort(field)}
                style={{ cursor: "pointer" }}
              >
                {field}{" "}
                {sortField === field && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedJobs.map((job) => {
            const selected = isJobSelected(job);
            return (
              <React.Fragment key={job.id}>
                <tr
                  onClick={(e) => handleRowClick(job.id, e)}
                  className={`clickable-row ${
                    expandedJobId === job.id ? "expanded" : ""
                  } ${selected ? "selected-row" : ""}`}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleCheckboxChange(job)}
                    />
                  </td>
                  <td>
                    <span
                      className={`status-pill ${getStatusClass(job.Status)}`}
                    >
                      {job.Status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{job.Name}</td>
                  <td>{job.SchedulerName}</td>
                  <td>{job.StartTime}</td>
                  <td>{job.EndTime}</td>
                  <td>{job.Duration}</td>
                  <td>{job.Runs}</td>
                </tr>

                {expandedJobId === job.id && (
                  <CellJob
                    job={job}
                    dictJobs={dictJobs}
                    colSpan={8}
                    onStatusChangeRequest={onStatusChangeRequest}
                    onAuthError={onAuthError}
                    idToken={idToken}
                    getToken={getToken}
                  />
                )}
              </React.Fragment>
            );
          })}
          {sortedJobs.length === 0 && (
            <tr>
              <td colSpan={8} className="no-data">
                No jobs found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableScheduler;
